import { useCallback, useEffect, useState } from 'react'
import { uniq, first } from 'lodash'
import currentGradYear from '../../../../utilities/current-grad-year'
import isEmailValid from '../../../../utilities/is-email-valid'
import GRADE_LEVELS from '../../../../constants/grade-levels'

const COLUMN_TYPES = [
    { label: 'First Name', value: 'firstName' },
    { label: 'Last Name', value: 'lastName' },
    { label: 'Email', value: 'email' },
    { label: 'Student ID', value: 'studentId' },
    { label: 'Graduation Year', value: 'gradYear' },
    { label: 'Grade Level', value: 'gradeLevel' },
]

const mapRecordsToUsers = (records, columnMap) => {
    const users = []
    records.map( columns => {
        const user = {}
        columns.map( (column, index) => {
            const columnName = columnMap[index]
            if (columnName) {
                user[columnName] = column
            }
        })
        users.push(user)
    })
    return users
}

const sanitizeUser = (user) => {
    return {
        ...user,
        email: user.email ? user.email.toLowerCase() : user.email,
        firstName: user.firstName ? user.firstName.toLowerCase() : user.firstName,
        lastName: user.lastName ? user.lastName.toLowerCase() : user.lastName,
        rolesCode: 's',
    }
}

const mapGradeLevelToGradYear = (user) => {
    const gradeLevelOffset = user.gradeLevel ? GRADE_LEVELS[user.gradeLevel.toUpperCase()] || 0 : 0
    return {
        ...user,
        gradYear: user.gradeLevel ? currentGradYear + gradeLevelOffset : user.gradYear,
        gradeLevel: undefined,
    }
}

const useMapper = ({
    records = [],
    onMap = () => {}
} = {}) => {

    /*
        Intended map structure:
        {
            1: 'firstName',
            3: 'email',
        }
    */
    const [columnMap, setColumnMap] = useState({})
    const [columnsSelected, setColumnsSelected] = useState([])
    const [selectedRows, setSelectedRows] = useState([])

    const setColumnType = useCallback((index, type) => {
        setColumnMap({ ...columnMap, [index]: type ? type : undefined })
    }, [columnMap, setColumnMap])

    const getColumnType = useCallback((index) => columnMap[index], [columnMap])

    const selectRow = useCallback((row, selected) => {
        if (selected) {
            setSelectedRows(uniq([...selectedRows, row]))
        } else {
            setSelectedRows(selectedRows.filter(r => r !== row ))
        }
    }, [selectedRows, setSelectedRows])

    const rowSelected = useCallback((row) => selectedRows.includes(row), [selectedRows])

    //  Detect when records changes and select all rows, and all columns (if there are not to many of course)
    useEffect(() => {
        if (records && records.length) {
            //  Get all records that don't have a valid email
            const validRecords = records.map((record, index) => ({ index, hasEmail: record.some(isEmailValid)})).filter(r => r.hasEmail).map(r => r.index)
            setSelectedRows(validRecords)


            const record = first(records)
            if (record.length < 8) {
                setColumnsSelected(Array.from(Array(record.length).keys()))
            }
        }
    }, [records])

    //  Do all the work of mapping here
    useEffect(() => {
        if (records && records.length) {
            //  Map the columns first
            let users = mapRecordsToUsers(records, columnMap)
            //  remove unselected records
            users = users.filter((u, index) => selectedRows.includes(index))
            //  Remove users without an email
            users = users.filter(u => Boolean(u.email))
            //  Sanitize
            users = users.map(sanitizeUser)
            //  map grade level to grad year (if possible)
            users = users.map(mapGradeLevelToGradYear)

            onMap(users)
        }
    }, [columnMap, records, onMap, columnsSelected, selectedRows])

    //  Map the records to users
    const users = mapRecordsToUsers(records, columnMap)

    return {
        columnTypeOptions: COLUMN_TYPES,
        setColumnType,
        getColumnType,
        users,
        columnsSelected,
        setColumnsSelected,
        selectRow,
        rowSelected,
        columnMap,
    }
}

export default useMapper